import React, { useState } from 'react';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const ApiKeySection = () => {
  const [apiKeys, setApiKeys] = useState([
    {
      id: 1,
      name: "Production Integration",
      key: "cg_prod_1234567890abcdef",
      created: "2024-12-15",
      lastUsed: "2025-01-19 10:30:00",
      usage: 1247,
      status: "active",
      permissions: ["read:incidents", "write:incidents", "read:reports"]
    },
    {
      id: 2,
      name: "Development Testing",
      key: "cg_dev_abcdef1234567890",
      created: "2025-01-10",
      lastUsed: "2025-01-18 15:45:00",
      usage: 89,
      status: "active",
      permissions: ["read:incidents", "read:reports"]
    },
    {
      id: 3,
      name: "Legacy SIEM Integration",
      key: "cg_legacy_fedcba0987654321",
      created: "2024-08-22",
      lastUsed: "2025-01-15 08:20:00",
      usage: 5632,
      status: "inactive",
      permissions: ["read:incidents"]
    }
  ]);

  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newKeyData, setNewKeyData] = useState({
    name: '',
    permissions: []
  });
  const [showKeyValue, setShowKeyValue] = useState({});

  const availablePermissions = [
    { value: 'read:incidents', label: 'Read Incidents', description: 'View incident data and details' },
    { value: 'write:incidents', label: 'Write Incidents', description: 'Create and update incidents' },
    { value: 'delete:incidents', label: 'Delete Incidents', description: 'Remove incidents (admin only)' },
    { value: 'read:reports', label: 'Read Reports', description: 'Access analytics and reports' },
    { value: 'read:users', label: 'Read Users', description: 'View user information' },
    { value: 'write:users', label: 'Write Users', description: 'Manage user accounts (admin only)' }
  ];

  const handleCreateKey = () => {
    if (!newKeyData?.name?.trim()) return;

    const newKey = {
      id: Date.now(),
      name: newKeyData?.name,
      key: `cg_${Date.now()?.toString(36)}_${Math.random()?.toString(36)?.substr(2, 16)}`,
      created: new Date()?.toISOString()?.split('T')?.[0],
      lastUsed: "Never",
      usage: 0,
      status: "active",
      permissions: newKeyData?.permissions
    };

    setApiKeys(prev => [newKey, ...prev]);
    setNewKeyData({ name: '', permissions: [] });
    setShowCreateForm(false);
    setShowKeyValue({ [newKey?.id]: true });
  };

  const handleDeleteKey = (keyId) => {
    setApiKeys(prev => prev?.filter(key => key?.id !== keyId));
  };

  const handleRegenerateKey = (keyId) => {
    setApiKeys(prev => prev?.map(key => 
      key?.id === keyId 
        ? { 
            ...key, 
            key: `cg_${Date.now()?.toString(36)}_${Math.random()?.toString(36)?.substr(2, 16)}`,
            lastUsed: "Never",
            usage: 0
          }
        : key
    ));
    setShowKeyValue({ [keyId]: true });
  };

  const handleToggleStatus = (keyId) => {
    setApiKeys(prev => prev?.map(key => 
      key?.id === keyId 
        ? { ...key, status: key?.status === 'active' ? 'inactive' : 'active' }
        : key
    ));
  };

  const handlePermissionChange = (permission, checked) => {
    setNewKeyData(prev => ({
      ...prev,
      permissions: checked 
        ? [...prev?.permissions, permission]
        : prev?.permissions?.filter(p => p !== permission)
    }));
  };

  const copyToClipboard = (text) => {
    navigator.clipboard?.writeText(text);
    console.log('API key copied to clipboard');
  };

  const getStatusColor = (status) => {
    return status === 'active' ? 'text-success' : 'text-muted-foreground';
  };

  const getStatusBg = (status) => {
    return status === 'active' ? 'bg-success' : 'bg-muted';
  };

  return (
    <div className="bg-card rounded-lg border border-border p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <div className="w-12 h-12 bg-secondary rounded-full flex items-center justify-center">
            <Icon name="Key" size={24} color="white" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-foreground">API Key Management</h3>
            <p className="text-sm text-muted-foreground">Manage API keys for system integrations</p>
          </div>
        </div>
        <Button
          variant="default"
          iconName="Plus"
          iconPosition="left"
          onClick={() => setShowCreateForm(true)}
        >
          Create New Key
        </Button>
      </div>
      {/* Create New Key Form */}
      {showCreateForm && (
        <div className="mb-6 p-4 bg-muted rounded-lg border border-border">
          <h4 className="font-medium text-foreground mb-4">Create New API Key</h4>
          
          <div className="space-y-4">
            <Input
              label="Key Name"
              type="text"
              placeholder="e.g., Production Integration"
              value={newKeyData?.name}
              onChange={(e) => setNewKeyData(prev => ({ ...prev, name: e?.target?.value }))}
              required
            />

            <div>
              <label className="block text-sm font-medium text-foreground mb-3">Permissions</label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {availablePermissions?.map((permission) => (
                  <div key={permission?.value} className="flex items-start space-x-3">
                    <input
                      type="checkbox"
                      id={permission?.value}
                      checked={newKeyData?.permissions?.includes(permission?.value)}
                      onChange={(e) => handlePermissionChange(permission?.value, e?.target?.checked)}
                      className="mt-1 w-4 h-4 text-primary bg-background border-border rounded focus:ring-primary focus:ring-2"
                    />
                    <div>
                      <label htmlFor={permission?.value} className="text-sm font-medium text-foreground cursor-pointer">
                        {permission?.label}
                      </label>
                      <p className="text-xs text-muted-foreground">{permission?.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex items-center space-x-3">
              <Button
                variant="default"
                onClick={handleCreateKey}
                disabled={!newKeyData?.name?.trim()}
              >
                Create API Key
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  setShowCreateForm(false);
                  setNewKeyData({ name: '', permissions: [] });
                }}
              >
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}
      {/* API Keys List */}
      <div className="space-y-4">
        {apiKeys?.map((apiKey) => (
          <div key={apiKey?.id} className="p-4 bg-background rounded-lg border border-border">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-3">
                <div className={`w-3 h-3 rounded-full ${getStatusBg(apiKey?.status)}`} />
                <div>
                  <h4 className="font-medium text-foreground">{apiKey?.name}</h4>
                  <p className="text-sm text-muted-foreground">
                    Created: {apiKey?.created} • Last used: {apiKey?.lastUsed}
                  </p>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <span className={`text-sm font-medium ${getStatusColor(apiKey?.status)}`}>
                  {apiKey?.status?.charAt(0)?.toUpperCase() + apiKey?.status?.slice(1)}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleToggleStatus(apiKey?.id)}
                >
                  {apiKey?.status === 'active' ? 'Deactivate' : 'Activate'}
                </Button>
              </div>
            </div>

            <div className="space-y-3">
              {/* API Key Display */}
              <div className="flex items-center space-x-2">
                <div className="flex-1 font-mono text-sm bg-muted p-3 rounded border">
                  {showKeyValue?.[apiKey?.id] ? apiKey?.key : '••••••••••••••••••••••••••••••••'}
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  iconName={showKeyValue?.[apiKey?.id] ? "EyeOff" : "Eye"}
                  onClick={() => setShowKeyValue(prev => ({ ...prev, [apiKey?.id]: !prev?.[apiKey?.id] }))}
                />
                <Button
                  variant="outline"
                  size="sm"
                  iconName="Copy"
                  onClick={() => copyToClipboard(apiKey?.key)}
                />
              </div>

              {/* Usage Stats */}
              <div className="flex items-center justify-between text-sm">
                <div className="flex items-center space-x-4">
                  <span className="text-muted-foreground">
                    Usage: <span className="font-medium text-foreground">{apiKey?.usage?.toLocaleString()} requests</span>
                  </span>
                  <span className="text-muted-foreground">
                    Permissions: <span className="font-medium text-foreground">{apiKey?.permissions?.length}</span>
                  </span>
                </div>
                <div className="flex items-center space-x-2">
                  <Button
                    variant="outline"
                    size="sm"
                    iconName="RefreshCw"
                    onClick={() => handleRegenerateKey(apiKey?.id)}
                  >
                    Regenerate
                  </Button>
                  <Button
                    variant="destructive"
                    size="sm"
                    iconName="Trash2"
                    onClick={() => handleDeleteKey(apiKey?.id)}
                  >
                    Delete
                  </Button>
                </div>
              </div>

              {/* Permissions List */}
              <div className="flex flex-wrap gap-2">
                {apiKey?.permissions?.map((permission) => (
                  <span
                    key={permission}
                    className="px-2 py-1 text-xs font-medium bg-primary text-primary-foreground rounded-full"
                  >
                    {permission}
                  </span>
                ))}
              </div>
            </div>
          </div>
        ))}
      </div>
      {apiKeys?.length === 0 && (
        <div className="text-center py-12">
          <Icon name="Key" size={48} className="text-muted-foreground mx-auto mb-4" />
          <h4 className="text-lg font-medium text-foreground mb-2">No API Keys</h4>
          <p className="text-muted-foreground mb-4">Create your first API key to start integrating with external systems.</p>
          <Button
            variant="default"
            iconName="Plus"
            iconPosition="left"
            onClick={() => setShowCreateForm(true)}
          >
            Create API Key
          </Button>
        </div>
      )}
    </div>
  );
};

export default ApiKeySection;